import {
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { styled } from '@mui/material/styles';
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { CustomTextField } from '../styles/SharedStyles';

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '8px',
  color: '#fff',
  '& .MuiSelect-icon': {
    color: 'var(--primary-color)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--secondary-color)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--secondary-color)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--primary-color)',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.8rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    color: '#fff',
    '& fieldset': {
      borderColor: 'var(--secondary-color)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--secondary-color)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--primary-color)',
    },
  },
  '& .MuiAutocomplete-popupIndicator': {
    color: 'var(--primary-color)',
  },
  '& .MuiAutocomplete-clearIndicator': {
    color: 'var(--secondary-color)',
  },
}));

const dropdownPanelPaperSx = {
  fontSize: '0.8rem',
  bgcolor: 'var(--bg-secondary-color)',
  color: '#fff',
  border: '1px solid #454545',
  borderRadius: 2,
  padding: '0 8px',
};

const optionHighlightBg = 'var(--dropdown-option-selected-bg, #332b1a)';

const autocompleteListPaperSx = {
  ...dropdownPanelPaperSx,
  overflow: 'hidden',
  '& .MuiAutocomplete-noOptions': {
    color: 'var(--secondary-color)',
  },
  '& .MuiAutocomplete-listbox': {
    maxWidth: '100%',
    '& .MuiAutocomplete-option': {
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      minHeight: 36,
      py: 0.75,
      minWidth: 0,
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      borderBottom: 'none',
    },
    '& .MuiAutocomplete-option.Mui-focused': {
      borderRadius: 2,
      bgcolor: optionHighlightBg,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      bgcolor: optionHighlightBg,
    },
    '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
      bgcolor: optionHighlightBg,
    },
  },
};

const selectMenuPaperSx = {
  ...dropdownPanelPaperSx,
  mt: 0.5,
  overflow: 'hidden',
  '& .MuiList-root': {
    py: 0.5,
  },
  '& .MuiMenuItem-root': {
    color: '#fff',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: 36,
    py: 0.75,
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 2,
    borderBottom: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
      bgcolor: '#2A2D2E',
    },
    '&.Mui-selected': {
      bgcolor: optionHighlightBg,
      '&:hover': {
        bgcolor: optionHighlightBg,
      },
    },
    '&.Mui-focusVisible': {
      bgcolor: optionHighlightBg,
    },
  },
};

export const CustomSelectField = ({
  id,
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  options = [],
  optionValueKey = 'value',
  optionLabelKey = 'label',
  fullWidth = true,
  searchable = false,
  disabled = false,
  placeholderLabel = '-- 请选择 --',
  includeEmptyOption = true,
}) => {
  // Only explicit false hides the row; null/undefined from spreads must not use `prop && <MenuItem />`
  const showEmptyOption = includeEmptyOption !== false;

  const labelId = `${id}-label`;
  const hasValue = value !== '' && value !== null && value !== undefined;
  const selectedOption =
    options.find((option) => {
      const optionValue =
        typeof option === 'object' ? option[optionValueKey] : option;
      return optionValue === value;
    }) ?? null;

  const formControlRef = useRef(null);
  const [selectMenuWidthPx, setSelectMenuWidthPx] = useState(null);

  const handleSelectOpen = useCallback(() => {
    const el = formControlRef.current;
    if (!el) return;
    const w = el.getBoundingClientRect().width;
    setSelectMenuWidthPx(w);
  }, []);

  const selectMenuProps = useMemo(
    () => ({
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      PaperProps: {
        sx: {
          ...selectMenuPaperSx,
          ...(selectMenuWidthPx != null && {
            width: `${selectMenuWidthPx}px`,
            minWidth: `${selectMenuWidthPx}px`,
            maxWidth: `${selectMenuWidthPx}px`,
            boxSizing: 'border-box',
          }),
        },
      },
    }),
    [selectMenuWidthPx],
  );

  useLayoutEffect(() => {
    if (searchable) return;
    const el = formControlRef.current;
    if (!el) return;
    setSelectMenuWidthPx(el.getBoundingClientRect().width);
  }, [searchable, fullWidth, options.length, id]);

  useLayoutEffect(() => {
    if (showEmptyOption) return;
    if (!options?.length) return;
    if (hasValue) return;
    const first = options[0];
    const firstVal =
      typeof first === 'object' ? first[optionValueKey] : first;
    if (firstVal === undefined || firstVal === null) return;
    onChange?.({ target: { value: firstVal } });
  }, [showEmptyOption, options, hasValue, optionValueKey, onChange]);

  if (searchable) {
    return (
      <StyledAutocomplete
        id={id}
        fullWidth={fullWidth}
        options={options}
        disabled={disabled}
        noOptionsText="无匹配"
        value={hasValue ? selectedOption : null}
        onChange={(event, newValue) => {
          const nextValue =
            newValue == null
              ? ''
              : typeof newValue === 'object'
                ? newValue?.[optionValueKey]
                : newValue;
          onChange?.({ target: { value: nextValue } });
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        getOptionLabel={(option) => {
          const optionLabel =
            typeof option === 'object' ? option[optionLabelKey] : option;
          return optionLabel != null ? String(optionLabel) : '';
        }}
        isOptionEqualToValue={(option, selected) => {
          const optionValue =
            typeof option === 'object' ? option[optionValueKey] : option;
          const selectedValue =
            typeof selected === 'object' ? selected?.[optionValueKey] : selected;
          return optionValue === selectedValue;
        }}
        slotProps={{
          paper: {
            sx: autocompleteListPaperSx,
          },
        }}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label={label}
            placeholder={placeholderLabel}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        )}
      />
    );
  }

  return (
    <FormControl ref={formControlRef} fullWidth={fullWidth} disabled={disabled}>
      <InputLabel
        id={labelId}
        sx={{
          color: 'var(--secondary-color)',
          '&.Mui-focused': { color: 'var(--primary-color)' },
          '&.Mui-error': { color: 'var(--error-color)' },
        }}
      >
        {label}
      </InputLabel>
      <StyledSelect
        id={id}
        labelId={labelId}
        value={value}
        label={label}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        displayEmpty
        onOpen={handleSelectOpen}
        MenuProps={selectMenuProps}
        renderValue={(selected) => {
          const isEmpty =
            selected === '' || selected === null || selected === undefined;
          if (isEmpty) return showEmptyOption ? placeholderLabel : '';

          const match = options.find((option) => {
            const optionValue =
              typeof option === 'object' ? option[optionValueKey] : option;
            return optionValue === selected;
          });
          if (!match) return String(selected);

          const optionLabel =
            typeof match === 'object' ? match[optionLabelKey] : match;
          return optionLabel != null ? String(optionLabel) : '';
        }}
      >
        {showEmptyOption ? (
          <MenuItem value="">
            {placeholderLabel}
          </MenuItem>
        ) : null}
        {options.map((option, index) => {
          const optionValue =
            typeof option === 'object' ? option[optionValueKey] : option;
          const optionLabel =
            typeof option === 'object' ? option[optionLabelKey] : option;

          return (
            <MenuItem key={`${optionValue}-${index}`} value={optionValue}>
              {optionLabel}
            </MenuItem>
          );
        })}
      </StyledSelect>
    </FormControl>
  );
};
